import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../services/api'
import CycleReportPdfButton from './CycleReportPdfButton'
import { formatDate } from './cycleStatus'
import { checklistReason, checklistTestLabel, stepWhen, toLocalInput } from './supplementaryFormat'

// VH-234: tests to request from the finalized health report — ordered ticks, done when an
// attached supplementary test contains them, planned draw date and dated preparation steps.
export function TestsToRequestSection({ cycleId, cycleDate = null }) {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const queryClient = useQueryClient()
  const printRef = useRef(null)
  const [error, setError] = useState(null)
  const queryKey = ['healthCycleTestChecklist', cycleId, language]

  const { data } = useQuery({
    queryKey,
    queryFn: () => healthCyclesAPI.getTestChecklist(cycleId, language),
    select: (response) => response.data,
    enabled: !!cycleId,
  })

  const store = (response) => {
    setError(null)
    queryClient.setQueryData(queryKey, response)
  }
  const tick = useMutation({
    mutationFn: ({ code, ordered }) => healthCyclesAPI.setTestOrdered(cycleId, code, ordered, language),
    onMutate: async ({ code, ordered }) => {
      const previous = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, (old) => (old ? {
        ...old, data: { ...old.data, tests: old.data.tests.map((x) => (x.code === code ? { ...x, ordered } : x)) },
      } : old))
      return { previous }
    },
    onSuccess: store,
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(queryKey, context?.previous)  // the tick reverts
      setError(t('healthCycle.testsToRequest.saveFailed'))
    },
  })
  const draw = useMutation({
    mutationFn: (value) => healthCyclesAPI.setPlannedDraw(cycleId, value, language),
    onSuccess: store,
    onError: () => setError(t('healthCycle.testsToRequest.saveFailed')),
  })

  if (!data?.available) return null
  const stamp = t('healthCycle.testsToRequest.stamp', { date: formatDate(cycleDate, language) })

  return (
    <section className="card" aria-label={t('healthCycle.testsToRequest.title')} data-testid="tests-to-request">
      <div className="card-body space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.testsToRequest.title')}</h3>
            <p className="text-xs text-gray-500">{t('healthCycle.testsToRequest.intro')}</p>
          </div>
          <CycleReportPdfButton targetRef={printRef} stamp={stamp} fileName={`tests-to-request-cycle-${cycleId}.pdf`} />
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-gray-700">
            {t('healthCycle.testsToRequest.drawDate')}
            <input type="datetime-local" className="mt-1 block text-sm" data-testid="planned-draw-input"
              value={toLocalInput(data.planned_draw_at)} disabled={draw.isPending}
              onChange={(e) => e.target.value && draw.mutate(`${e.target.value}:00`)} />
          </label>
          {data.planned_draw_at && (
            <button type="button" className="btn-outline text-xs" disabled={draw.isPending} onClick={() => draw.mutate(null)}>
              {t('healthCycle.testsToRequest.clearDraw')}
            </button>
          )}
        </div>
        {data.planned_draw_in_past && <p className="text-sm text-amber-700" role="status">{t('healthCycle.testsToRequest.drawInPast')}</p>}

        <div ref={printRef} className="space-y-3 bg-surface">
          <ul className="space-y-2">
            {data.tests.map((test) => (
              <li key={test.code} className="rounded-md border border-gray-200 p-3" data-testid={`checklist-test-${test.code}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <label className="flex min-w-0 items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={test.ordered}
                      onChange={(e) => tick.mutate({ code: test.code, ordered: e.target.checked })} />
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900">{checklistTestLabel(t, test, language)}</span>
                      {checklistReason(t, test, language) && (
                        <span className="block text-xs text-gray-600">{checklistReason(t, test, language)}</span>
                      )}
                    </span>
                  </label>
                  {test.done ? (
                    <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                      {t('healthCycle.testsToRequest.done', { date: formatDate(test.done_on, language) })}
                    </span>
                  ) : (
                    <span className={`rounded px-2 py-0.5 text-xs ${test.ordered ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-700'}`}>
                      {test.ordered ? t('healthCycle.testsToRequest.ordered') : t('healthCycle.testsToRequest.open')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {data.steps.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase text-gray-600">{t('healthCycle.testsToRequest.preparation')}</h4>
              <ul className="mt-1 space-y-1 text-sm text-gray-800">
                {data.steps.map((step) => (
                  <li key={step.rule_code ?? step.text} data-testid={`prep-step-${step.rule_code}`}>
                    <span className="font-medium">{stepWhen(t, step, language)}</span>: {step.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default TestsToRequestSection
